import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { AttachFilesSection } from './AttachFilesSection'
import type { AttachmentRef } from './types'
import type { AttachmentTarget } from '@/types'
import { clientsApi } from '@/services/clients'
import { settingsApi } from '@/services/settings'
import { DOCUMENT_FILE_ACCEPT } from '@/lib/constants'
import { fileNameBase } from '@/lib/documentRename'

export interface PendingAttachFilesHandle {
  /**
   * Persist every locally-collected attachment against the entity that has just
   * been created. Upload-new picks are uploaded; client / library picks are
   * linked by file id. Resolves when all are persisted (rejects on the first
   * failure so the caller can surface it).
   */
  flush: (entityId: string) => Promise<void>
  /** How many attachments are queued (so the parent can skip flush when empty). */
  count: () => number
}

interface Props {
  attachTo: AttachmentTarget
  /** Client whose documents back the "From client documents" tab. */
  clientId?: string
  /** Entity-specific upload endpoint, invoked once the entity exists. */
  uploadFn: (entityId: string, file: File) => Promise<unknown>
  /** Entity-specific "link an existing file" endpoint. */
  attachExistingFn: (
    entityId: string,
    fileId: string,
    source: 'client' | 'library'
  ) => Promise<unknown>
  /** Existing entity id. When set, changes remain local until flush(). */
  entityId?: string
  /** Load the entity's currently-persisted documents for edit mode. */
  listExistingFn?: (entityId: string) => Promise<{
    data?: ExistingDocument[]
  }>
  /** Remove a persisted document when the staged form is saved. */
  removeFn?: (entityId: string, documentId: string) => Promise<unknown>
  /** Rename a persisted upload when the staged form is saved. */
  renameFn?: (
    entityId: string,
    documentId: string,
    name: string
  ) => Promise<unknown>
  accept?: string
  hideHeading?: boolean
  brandTabs?: boolean
  uploadTabLabel?: string
  onCountChange?: (count: number) => void
}

interface ExistingDocument {
  id?: string
  file_id?: string
  file_name?: string | null
  url?: string | null
  file_type?: string | null
  file_size?: number | null
  created_at?: string | null
  doc_type?: string | null
}

function attachmentSource(
  docType: string | null | undefined
): AttachmentRef['source'] {
  if (docType === 'client' || docType === 'library') return docType
  return 'upload'
}

function toAttachmentRef(document: ExistingDocument): AttachmentRef {
  return {
    id: document.id,
    file_id: document.file_id,
    name: document.file_name ?? '',
    url: document.url,
    mime_type: document.file_type,
    size: document.file_size,
    created_at: document.created_at,
    source: attachmentSource(document.doc_type),
  }
}

function sameAttachment(a: AttachmentRef, b: AttachmentRef): boolean {
  if (a.file_id && b.file_id) return a.file_id === b.file_id
  if (a.id && b.id) return a.id === b.id
  return a.name === b.name && (a.url ?? '') === (b.url ?? '')
}

// Form attachment picker. It collects changes locally in both create and edit
// modes, then exposes flush(entityId) so the parent persists them only after
// the owning form has saved successfully.
export const PendingAttachFiles = forwardRef<PendingAttachFilesHandle, Props>(
  function PendingAttachFiles(
    {
      attachTo,
      clientId,
      uploadFn,
      attachExistingFn,
      entityId,
      listExistingFn,
      removeFn,
      renameFn,
      accept,
      hideHeading,
      brandTabs,
      uploadTabLabel,
      onCountChange,
    },
    ref
  ) {
    const [value, setValue] = useState<AttachmentRef[]>([])
    const originalValue = useRef<AttachmentRef[]>([])

    useEffect(() => {
      if (!entityId || !listExistingFn) {
        originalValue.current = []
        return
      }

      let active = true
      listExistingFn(entityId)
        .then(response => {
          if (!active) return
          const existing = (response.data ?? []).map(toAttachmentRef)
          originalValue.current = existing
          setValue(current => [
            ...existing,
            ...current.filter(
              item => !existing.some(saved => sameAttachment(saved, item))
            ),
          ])
        })
        .catch(() => {
          // Match the existing attachment panels: leave the list empty when
          // loading fails and let the main form remain usable.
        })

      return () => {
        active = false
      }
    }, [entityId, listExistingFn])

    useEffect(() => {
      onCountChange?.(value.length)
    }, [onCountChange, value.length])

    // Defer the upload: keep the raw File on the ref and upload it on flush.
    const pendingUpload = useCallback(
      async (file: File): Promise<AttachmentRef> => ({
        name: file.name,
        size: file.size,
        mime_type: file.type,
        source: 'upload',
        file,
        created_at: new Date(file.lastModified).toISOString(),
      }),
      []
    )

    const listClientDocsFn = useCallback(
      async (cId: string): Promise<AttachmentRef[]> => {
        const res = await clientsApi.documents(cId, { limit: 100 })
        return (res.data ?? []).map(d => ({
          id: d.file_id,
          file_id: d.file_id,
          name: d.file_name,
          url: d.url,
          mime_type: d.file_type,
          size: d.file_size,
          source: 'client' as const,
          created_at: d.created_at,
        }))
      },
      []
    )

    const listLibraryFn = useCallback(async (): Promise<AttachmentRef[]> => {
      const res = await settingsApi.reusableDocumentOptions(attachTo)
      return (res.data ?? []).map(d => ({
        id: d.file_id,
        file_id: d.file_id,
        name: d.name,
        source: 'library' as const,
        created_at: d.created_at,
      }))
    }, [attachTo])

    useImperativeHandle(
      ref,
      () => ({
        count: () => {
          const original = originalValue.current
          const added = value.filter(
            item =>
              !!item.file ||
              !original.some(saved => sameAttachment(saved, item))
          )
          const removed = original.filter(
            saved => !value.some(item => sameAttachment(saved, item))
          )
          const renamed = value.filter(item => {
            const saved = original.find(candidate =>
              sameAttachment(candidate, item)
            )
            return !!saved && saved.name !== item.name
          })
          return added.length + removed.length + renamed.length
        },
        flush: async (entityId: string) => {
          const original = originalValue.current
          for (const r of value) {
            const saved = original.find(candidate =>
              sameAttachment(candidate, r)
            )
            if (r.file) {
              await uploadFn(entityId, r.file)
            } else if (
              !saved &&
              (r.source === 'client' || r.source === 'library')
            ) {
              const fileId = r.file_id ?? r.id
              if (fileId) await attachExistingFn(entityId, fileId, r.source)
            } else if (
              saved &&
              saved.name !== r.name &&
              r.source === 'upload' &&
              saved.id &&
              renameFn
            ) {
              await renameFn(entityId, saved.id, fileNameBase(r.name))
            }
          }

          if (removeFn) {
            for (const saved of original) {
              if (
                saved.id &&
                !value.some(item => sameAttachment(saved, item))
              ) {
                await removeFn(entityId, saved.id)
              }
            }
          }
        },
      }),
      [value, uploadFn, attachExistingFn, removeFn, renameFn]
    )

    return (
      <AttachFilesSection
        value={value}
        onChange={setValue}
        clientId={clientId}
        accept={accept ?? DOCUMENT_FILE_ACCEPT}
        uploadFn={pendingUpload}
        listClientDocsFn={clientId ? listClientDocsFn : undefined}
        listLibraryFn={listLibraryFn}
        libraryTarget={attachTo}
        preloadLibrary
        onRename={() => undefined}
        hideHeading={hideHeading}
        brandTabs={brandTabs}
        uploadTabLabel={uploadTabLabel}
      />
    )
  }
)

export default PendingAttachFiles
